/**
 * 計助ワークスペース: 3×2 分割レイアウトの localStorage 永続化と旧形式マイグレーション
 */

import { safeLocalStorageSetJson } from './keisukeDashboardLocalStorage';

/** カラム内の上下分割比を [0.2, 0.8] に収める */
export function sanitizeColumnVerticalRatio(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return Math.max(0.2, Math.min(0.8, fallback));
  return Math.max(0.2, Math.min(0.8, n));
}

export const LAYOUT_STORAGE_KEY = 'keisuke-dashboard-layout';

export const DEFAULT_LEFT_COLUMN_RATIO = 0.18;
/** 左レールが薄い t検定時も右グラフが窮屈にならないよう、中央をやや抑える */
export const DEFAULT_CENTER_COLUMN_RATIO = 0.5;
export const DEFAULT_LEFT_ANALYSIS_FILE_RATIO = 0.5;
export const DEFAULT_CENTER_DATA_RESULTS_RATIO = 0.62;
/** 設定帯を開いたときの本図側の割合（大きいほど本図が広い） */
export const DEFAULT_RIGHT_CHART_SETTINGS_RATIO = 0.78;

export const LEFT_COLUMN_RATIO_MIN = 0.15;
export const LEFT_COLUMN_RATIO_MAX = 0.38;
export const CENTER_COLUMN_RATIO_MIN = 0.35;
export const CENTER_COLUMN_RATIO_MAX = 0.75;

export type KeisukeDashboardLayoutPersisted = {
  leftColumnRatio: number;
  centerColumnRatio: number;
  leftAnalysisFileRatio: number;
  centerDataResultsRatio: number;
  rightChartSettingsRatio: number;
};

type LegacyLayoutRaw = Record<string, unknown>;

export function sanitizeLeftColumnRatio(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_LEFT_COLUMN_RATIO;
  return Math.max(LEFT_COLUMN_RATIO_MIN, Math.min(LEFT_COLUMN_RATIO_MAX, n));
}

export function sanitizeCenterColumnRatio(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_CENTER_COLUMN_RATIO;
  /* 旧既定 0.58 は右グラフが窮屈だった → 新既定へ一度だけ寄せる */
  if (Math.abs(n - 0.58) < 1e-9) return DEFAULT_CENTER_COLUMN_RATIO;
  return Math.max(CENTER_COLUMN_RATIO_MIN, Math.min(CENTER_COLUMN_RATIO_MAX, n));
}

function migrateLegacyLayout(raw: LegacyLayoutRaw): Partial<KeisukeDashboardLayoutPersisted> {
  const out: Partial<KeisukeDashboardLayoutPersisted> = {};

  if (typeof raw.leftColumnRatio === 'number') {
    out.leftColumnRatio = sanitizeLeftColumnRatio(raw.leftColumnRatio);
  }
  if (typeof raw.centerColumnRatio === 'number') {
    out.centerColumnRatio = sanitizeCenterColumnRatio(raw.centerColumnRatio);
  }
  if (typeof raw.leftAnalysisFileRatio === 'number') {
    out.leftAnalysisFileRatio = sanitizeColumnVerticalRatio(
      raw.leftAnalysisFileRatio,
      DEFAULT_LEFT_ANALYSIS_FILE_RATIO
    );
  }
  if (typeof raw.centerDataResultsRatio === 'number') {
    out.centerDataResultsRatio = sanitizeColumnVerticalRatio(
      raw.centerDataResultsRatio,
      DEFAULT_CENTER_DATA_RESULTS_RATIO
    );
  }
  if (typeof raw.rightChartSettingsRatio === 'number') {
    out.rightChartSettingsRatio = sanitizeColumnVerticalRatio(
      raw.rightChartSettingsRatio,
      DEFAULT_RIGHT_CHART_SETTINGS_RATIO
    );
  }

  if (out.leftColumnRatio == null && typeof raw.analysisPaneWidth === 'number') {
    const viewport =
      typeof window !== 'undefined' && window.innerWidth > 0 ? window.innerWidth : 1280;
    out.leftColumnRatio = sanitizeLeftColumnRatio(raw.analysisPaneWidth / viewport);
  }

  if (out.centerDataResultsRatio == null && typeof raw.graphSettingsRatio === 'number') {
    const legacyBottom = raw.graphSettingsRatio as number;
    out.centerDataResultsRatio = sanitizeColumnVerticalRatio(
      1 - legacyBottom,
      DEFAULT_CENTER_DATA_RESULTS_RATIO
    );
  }

  return out;
}

export function loadLayoutFromStorage(): Partial<KeisukeDashboardLayoutPersisted> {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as LegacyLayoutRaw;
    return migrateLegacyLayout(parsed);
  } catch {
    return {};
  }
}

export function persistLayoutToStorage(state: KeisukeDashboardLayoutPersisted): void {
  safeLocalStorageSetJson(LAYOUT_STORAGE_KEY, state);
}
